import React, { useEffect, useMemo, useRef, useState } from "react";
import { openAuthModal } from "../../common/authmodal/authmodalservice";
import StudyGateBadge from "../../common/StudyGateBadge/StudyGateBadge";
import {
  archiveStudyWithPayload,
  fetchStudyArchiveReadiness,
  isStudyAlreadyArchived,
} from "./studyArchiveService";
import {
  getStudySchedulePublishLabel,
  SCHEDULE_STATUS_DRAFT,
  SCHEDULE_STATUS_NONE,
  SCHEDULE_STATUS_PUBLISHED,
} from "./studyMgmtReportUtils";
import "./StudyArchiveWizard.scss";

function StatusChip({ ok, label, count }) {
  return (
    <span
      className={`study-archive-wizard__chip ${ok ? "is-ok" : "is-blocked"}`}
    >
      <i
        className={`fas ${ok ? "fa-check-circle" : "fa-exclamation-circle"}`}
        aria-hidden="true"
      />
      <span>{label}</span>
      {typeof count === "number" ? <em>{count}</em> : null}
    </span>
  );
}

const STAFF_AVATAR_TONES = [
  "#1e3a5f",
  "#0f766e",
  "#9a3412",
  "#0369a1",
  "#1d4ed8",
  "#b45309",
  "#be123c",
  "#334155",
];

function staffInitials(name = "") {
  const parts = String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
}

function staffAvatarColor(name = "") {
  const str = String(name);
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  return STAFF_AVATAR_TONES[hash % STAFF_AVATAR_TONES.length];
}

function SectionCard({ title, countLabel, statusOk, children }) {
  return (
    <section
      className={`study-archive-wizard__section ${
        statusOk ? "is-ready" : "is-blocked"
      }`}
    >
      <header className="study-archive-wizard__section-head">
        <div className="study-archive-wizard__section-title">
          <h6>{title}</h6>
          {countLabel ? (
            <span className="study-archive-wizard__section-count">
              {countLabel}
            </span>
          ) : null}
        </div>
        <StatusChip ok={statusOk} label={statusOk ? "Clear" : "Blocked"} />
      </header>
      <div className="study-archive-wizard__section-body">{children}</div>
    </section>
  );
}

function CheckIcon({ ok }) {
  return (
    <i
      className={`fas ${
        ok ? "fa-check-circle" : "fa-exclamation-circle"
      } study-archive-wizard__check-icon ${ok ? "is-ok" : "is-blocked"}`}
      aria-hidden="true"
    />
  );
}

function StudyTitleLine({ number, name, color }) {
  const title = String(name || "").trim() || "—";
  const studyNumber = String(number || "").trim();
  return (
    <div className="study-archive-wizard__study-line">
      {studyNumber ? (
        <span
          className="study-archive-wizard__number-badge"
          style={color ? { backgroundColor: color } : undefined}
          title={studyNumber}
        >
          {studyNumber}
        </span>
      ) : null}
      <span className="study-archive-wizard__study-name" title={title}>
        {title}
      </span>
    </div>
  );
}

function ScheduleStatusBadge({ status }) {
  const label =
    String(status || SCHEDULE_STATUS_NONE).trim() || SCHEDULE_STATUS_NONE;
  const tone =
    label === SCHEDULE_STATUS_PUBLISHED
      ? "published"
      : label === SCHEDULE_STATUS_DRAFT
        ? "draft"
        : "none";
  return (
    <span
      className={`study-archive-wizard__schedule-badge is-${tone}`}
      title={label}
    >
      {label}
    </span>
  );
}

function PendingItemRow({ task }) {
  const why =
    task.status === "Overdue"
      ? "Overdue"
      : task.type === "sign"
        ? "Signature pending"
        : "Upload pending";
  const rawDetail = [task.subtitle, task.folderName || task.folder]
    .map((part) => String(part || "").trim())
    .filter((part) => part && !/^[0-9a-f-]{36}$/i.test(part))
    .join(" · ");

  return (
    <li className="study-archive-wizard__check is-pending">
      <CheckIcon ok={false} />
      <div className="study-archive-wizard__check-body">
        <span className="study-archive-wizard__check-label" title={task.title}>
          {task.title || "Untitled item"}
        </span>
        <span className="study-archive-wizard__check-msg">
          {why}
          {rawDetail ? ` · ${rawDetail}` : ""}
        </span>
      </div>
    </li>
  );
}

function PendingCategoryPanel({ bundle, emptyLabel, title }) {
  const tasks = bundle?.tasks || [];
  const isClear = !tasks.length;
  const phase = bundle?.gateLabel || bundle?.gateCode || "";

  return (
    <div className="study-archive-wizard__panel">
      <div className="study-archive-wizard__panel-head">
        <span className="study-archive-wizard__panel-title">{title}</span>
        <span className="study-archive-wizard__panel-meta">
          {phase ? `${phase} · ` : ""}
          {isClear ? "0 open" : `${tasks.length} open`}
        </span>
      </div>

      {isClear ? (
        <div className="study-archive-wizard__empty">
          <CheckIcon ok />
          <span>{emptyLabel}</span>
        </div>
      ) : (
        <ul className="study-archive-wizard__checklist">
          {tasks.map((task) => (
            <PendingItemRow key={task.id} task={task} />
          ))}
        </ul>
      )}
    </div>
  );
}

function SummaryGrid({ summary, scheduleStatus = "" }) {
  if (!summary) return null;

  const statusLabel = String(summary.status || "—").trim() || "—";
  const statusTone = /archiv/i.test(statusLabel)
    ? "archive"
    : /live/i.test(statusLabel)
      ? "live"
      : "default";

  const pairRows = [
    {
      label: "Phase",
      value: (
        <StudyGateBadge
          code={summary.phaseCode || "PRE_STUDY"}
          label={summary.phaseLabel || summary.phaseCode}
          compact
        />
      ),
    },
    {
      label: "Status",
      value: (
        <span
          className={`study-archive-wizard__study-status is-${statusTone}`}
          title={statusLabel}
        >
          {statusLabel}
        </span>
      ),
    },
    {
      label: "Schedule",
      value: <ScheduleStatusBadge status={scheduleStatus} />,
    },
    { label: "Group", value: summary.groupType || "—" },
    { label: "PI", value: summary.piName || "—" },
    { label: "Sites", value: String(summary.siteCount ?? 0) },
    { label: "Staff", value: String(summary.staffCount ?? 0) },
    { label: "Sponsor", value: summary.sponsorName || "—" },
    { label: "IRAS", value: summary.irasNumber || "—" },
    { label: "EudraCT", value: summary.eudractNumber || "—" },
    { label: "R&D", value: summary.rdNumber || "—" },
    { label: "Department", value: summary.department || "—" },
    { label: "Coordinator", value: summary.coordinatorName || "—" },
    { label: "Type", value: summary.studyType || "—" },
    { label: "Protocol", value: summary.protocolNumber || "—" },
  ];

  const staffVisible = (summary.staff || []).filter((user) => {
    const name = String(user.UserName || user.EmailId || "").trim();
    return name && !/^[0-9a-f-]{36}$/i.test(name);
  });
  const piNameNorm = String(summary.piName || "")
    .trim()
    .toLowerCase();

  return (
    <div className="study-archive-wizard__summary">
      <div className="study-archive-wizard__summary-row study-archive-wizard__summary-row--title">
        <span className="study-archive-wizard__summary-label">Title</span>
        <StudyTitleLine
          number={summary.studyNumber}
          name={summary.studyName}
          color={summary.studyColor}
        />
      </div>

      <dl className="study-archive-wizard__summary-grid">
        {pairRows.map((row) => (
          <div key={row.label} className="study-archive-wizard__summary-row">
            <dt>{row.label}</dt>
            <dd title={typeof row.value === "string" ? row.value : undefined}>
              {row.value}
            </dd>
          </div>
        ))}
      </dl>

      {staffVisible.length ? (
        <div className="study-archive-wizard__staff">
          <div className="study-archive-wizard__staff-head">
            <span className="study-archive-wizard__staff-label">Staff</span>
            <span className="study-archive-wizard__staff-count">
              {staffVisible.length}
            </span>
          </div>
          <ul className="study-archive-wizard__staff-list">
            {staffVisible.slice(0, 10).map((user) => {
              const name = String(user.UserName || user.EmailId || "").trim();
              const email = String(user.EmailId || "").trim();
              const showEmail =
                email &&
                email.toLowerCase() !== name.toLowerCase() &&
                !/^[0-9a-f-]{36}$/i.test(email);
              const isPi = piNameNorm && name.toLowerCase() === piNameNorm;
              return (
                <li key={user.UserId} className="study-archive-wizard__staff-item">
                  <span
                    className="study-archive-wizard__staff-avatar"
                    style={{ background: staffAvatarColor(name) }}
                    aria-hidden="true"
                  >
                    {staffInitials(name)}
                  </span>
                  <span className="study-archive-wizard__staff-meta">
                    <span className="study-archive-wizard__staff-name">
                      {name}
                      {isPi ? (
                        <em className="study-archive-wizard__staff-role">PI</em>
                      ) : null}
                    </span>
                    {showEmail ? (
                      <span className="study-archive-wizard__staff-email">
                        {email}
                      </span>
                    ) : null}
                  </span>
                </li>
              );
            })}
            {staffVisible.length > 10 ? (
              <li className="study-archive-wizard__staff-item is-more">
                <span className="study-archive-wizard__staff-avatar is-more">
                  +
                </span>
                <span className="study-archive-wizard__staff-meta">
                  <span className="study-archive-wizard__staff-name">
                    {staffVisible.length - 10} more assigned
                  </span>
                </span>
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export default function StudyArchiveWizard({
  open,
  studyRow,
  userData,
  siteIds = null,
  scheduleStatusByStudyId = {},
  onClose,
  onArchived,
  showAlert,
}) {
  const [loading, setLoading] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [error, setError] = useState("");
  const [readiness, setReadiness] = useState(null);
  const loadSeqRef = useRef(0);

  useEffect(() => {
    if (!open || !studyRow || !userData?.OrganizationId) return undefined;

    if (isStudyAlreadyArchived(studyRow)) {
      setError("This study is already archived.");
      setReadiness(null);
      return undefined;
    }

    const seq = ++loadSeqRef.current;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");
      setReadiness(null);
      try {
        const result = await fetchStudyArchiveReadiness({
          userData,
          studyRow,
          siteIds,
        });
        if (cancelled || seq !== loadSeqRef.current) return;
        setReadiness(result);
      } catch (err) {
        if (cancelled || seq !== loadSeqRef.current) return;
        setError(err?.message || "Failed to load archive readiness checks.");
        setReadiness(null);
      } finally {
        if (!cancelled && seq === loadSeqRef.current) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, studyRow, userData, siteIds]);

  const canContinue = useMemo(
    () => Boolean(readiness?.canArchive) && !loading && !archiving && !error,
    [readiness, loading, archiving, error],
  );

  const scheduleStatus = useMemo(
    () =>
      getStudySchedulePublishLabel(studyRow, scheduleStatusByStudyId) ||
      SCHEDULE_STATUS_NONE,
    [studyRow, scheduleStatusByStudyId],
  );

  if (!open) return null;

  const studyLabel =
    studyRow?.StudyName || readiness?.summary?.studyName || "Study";
  const studyNumber =
    studyRow?.StudyNumber ||
    studyRow?.StudyCode ||
    readiness?.summary?.studyNumber ||
    "";
  const studyColor =
    studyRow?.StudySchColor ||
    studyRow?.StudyColor ||
    readiness?.summary?.studyColor ||
    "";

  const handleContinue = async () => {
    if (!canContinue || !readiness?.archivePayload) return;

    const authRes = await openAuthModal({
      showDescription: true,
      requireDescription: true,
      useMfa: true,
      descriptionLabel: "Reason for archiving",
      descriptionPlaceholder: "Enter why this study is being archived…",
      introMessage: "Confirm your identity to archive this study.",
    });

    if (authRes?.Success !== 1) {
      if (!authRes?.cancelled && showAlert) {
        showAlert({
          title: "Authentication required",
          message:
            "Multi-factor authentication failed or was cancelled. Study was not archived.",
          type: "warning",
        });
      }
      return;
    }

    const reason = String(authRes?.Description || "").trim();
    if (!reason) {
      showAlert?.({
        title: "Reason required",
        message: "A reason is required to archive this study.",
        type: "warning",
      });
      return;
    }

    setArchiving(true);
    setError("");
    try {
      const response = await archiveStudyWithPayload(
        readiness.archivePayload,
        reason,
      );
      const ok =
        Number(response?.success) === 1 || Number(response?.Success) === 1;

      if (!ok) {
        const message =
          response?.message ||
          response?.Message ||
          "Unable to archive the study. Please try again.";
        setError(message);
        showAlert?.({
          title: "Archive study",
          message,
          type: "danger",
        });
        return;
      }

      showAlert?.({
        title: "Study archived",
        message:
          response?.message ||
          `"${studyLabel}" has been archived successfully.`,
        type: "success",
      });
      onArchived?.({
        studyId: readiness.studyId,
        reason,
        response,
      });
      onClose?.();
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong while archiving the study.";
      setError(message);
      showAlert?.({
        title: "Archive study",
        message,
        type: "danger",
      });
    } finally {
      setArchiving(false);
    }
  };

  const isfCount = readiness?.isf?.pendingCount || 0;
  const psfCount = readiness?.psf?.pendingCount || 0;
  const docsPending = isfCount + psfCount;
  const apptCount = readiness?.appointments?.count || 0;
  const retentionYears = Number(readiness?.retentionPeriodYears) || 10;
  const retentionUntilLabel = (() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + retentionYears);
    return d.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  })();

  return (
    <div
      className="modal fade show d-block study-archive-wizard"
      style={{ backgroundColor: "rgba(15, 23, 42, 0.45)" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="study-archive-wizard-title"
    >
      <div className="modal-dialog modal-fullscreen-lg-down modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <div className="study-archive-wizard__heading">
              <h5 className="modal-title" id="study-archive-wizard-title">
                Archive study
              </h5>
              <StudyTitleLine
                number={studyNumber}
                name={studyLabel}
                color={studyColor}
              />
            </div>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
              disabled={archiving}
            />
          </div>

          <div className="modal-body">
            {loading ? (
              <p className="study-archive-wizard__loading">
                Checking readiness…
              </p>
            ) : null}

            {!loading && readiness ? (
              <>
              <div className="study-archive-wizard__columns">
                <SectionCard
                  title="eISF / ePSF"
                  countLabel={
                    docsPending ? `${docsPending} pending` : "Nothing pending"
                  }
                  statusOk={
                    Boolean(readiness.isf?.isClear) &&
                    Boolean(readiness.psf?.isClear)
                  }
                >
                  <div className="study-archive-wizard__stack">
                    <PendingCategoryPanel
                      title="eISF"
                      bundle={readiness.isf}
                      emptyLabel="No open eISF items"
                    />
                    <PendingCategoryPanel
                      title="ePSF"
                      bundle={readiness.psf}
                      emptyLabel="No open ePSF items"
                    />
                  </div>
                </SectionCard>

                <SectionCard
                  title="Appointments"
                  countLabel={
                    apptCount ? `${apptCount} confirmed` : "None confirmed"
                  }
                  statusOk={Boolean(readiness.appointments?.isClear)}
                >
                  {!readiness.studyScheduleId ? (
                    <div className="study-archive-wizard__empty">
                      <CheckIcon ok />
                      <span>No schedule — nothing to clear</span>
                    </div>
                  ) : readiness.appointments?.appointments?.length ? (
                    <ul className="study-archive-wizard__checklist">
                      {readiness.appointments.appointments.map((row) => (
                        <li
                          key={row.id}
                          className="study-archive-wizard__check is-pending"
                        >
                          <CheckIcon ok={false} />
                          <div className="study-archive-wizard__check-body">
                            <span className="study-archive-wizard__check-label">
                              {row.volunteerName}
                            </span>
                            <span className="study-archive-wizard__check-msg">
                              Confirmed · {row.visitName}
                              {row.startTime ? ` · ${row.startTime}` : ""}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="study-archive-wizard__empty">
                      <CheckIcon ok />
                      <span>No confirmed appointments</span>
                    </div>
                  )}
                </SectionCard>

                <SectionCard title="Summary" statusOk>
                  <SummaryGrid
                    summary={readiness.summary}
                    scheduleStatus={scheduleStatus}
                  />
                </SectionCard>
              </div>

              <div className="study-archive-wizard__retention" role="note">
                <i className="fas fa-info-circle" aria-hidden="true" />
                <div>
                  <strong>Unarchive window:</strong> after you archive this
                  study, you can unarchive it within{" "}
                  <strong>
                    {retentionYears} year{retentionYears === 1 ? "" : "s"}
                  </strong>{" "}
                  (until approximately {retentionUntilLabel}), based on your
                  organisation retention setting.
                </div>
              </div>
              </>
            ) : null}

            {error ? (
              <p className="study-archive-wizard__error">{error}</p>
            ) : null}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={onClose}
              disabled={archiving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={handleContinue}
              disabled={!canContinue}
            >
              {archiving ? "Archiving…" : "Authenticate & archive"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
